import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { sanitizeEmailArray } from "@sellar-io/common";
import { BasicDialog } from "../../../../components/modal/BasicDialog";
import { CancelOrderDialog } from "../../modals/CancelOrderDialog";
import { CancelRecreateOrderDialogActive } from "../../modals/CheckoutDialogs/CancelRecreateOrderDialog";
import { CheckoutOrderPartialRefundDialog } from "../../modals/CheckoutDialogs/CheckoutOrderPartialRefundDialog";
import { ModifyOrderDialog } from "../../modals/ModifyOrderDialog";
import { OrderCreateInvoiceDialog } from "../../modals/OrderCreateInvoiceDialog";
import { OrderDeliveredDialog } from "../../modals/OrderDeliveredDialog";
import { OrderEventListDialog } from "../../modals/OrderEvents/OrderEventListDialog";
import { OrderPaidModal } from "../../modals/OrderPaidModal";
import { XeroInvoiceDialog } from "../../modals/XeroInvoiceDialog";
import { CreateCreditNoteDialog } from "../../modals/CreateCreditNoteDialog";
import { SellarCreditOrderCreateInvoiceDialog } from "../../modals/SellarCreditOrderCreateInvoiceDialog";
import { SellarCreditCancelWarningDialog } from "../../modals/SellarCreditCancelWarningDialog";
import { IncompleteStripePaymentOrderPaidModal } from "../../modals/IncompleteStripePaymentOrderPaidModal";
import { CardPaymentWithDepositManualPaymentModal } from "../../modals/CardPaymentWithDepositManualPaymentModal";
import { AdminChargeRemainingOrderBalance } from "../../modals/AdminChargeRemainingOrderBalance";
import { CreateSellarImportCreditNoteDialog } from "../../modals/CreateSellarImportCreditNoteDialog";
import { AdminAddOrderToPallet } from "../../modals/AdminAddOrderToPallet/AdminAddOrderToPallet";
import { AdminRemoveOrderFromPallet } from "../../modals/AdminRemoveOrderFromPallet/AdminRemoveOrderFromPallet";
import { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import { OrderContentConnectionsType } from "../OrderContent";
import { OrderContentActions, OrderContentState } from "./useOrderContentState";
import { OrderAssignRouteAndCreateCTTaskDialog } from "../../modals/OrderAssignRouteAndCreateCTTaskDialog";

type OrderDialogsProps = {
  order: OrderGetResponseType;
  connection?: OrderContentConnectionsType;
  business: BusinessGetResponseType;
  state: OrderContentState;
  actions: OrderContentActions;
  getOrderCannotBeEditedMessage: () => string;
  onEditOrder: () => void;
};

export const OrderDialogs = ({
  order,
  connection,
  business,
  state,
  actions,
  getOrderCannotBeEditedMessage,
  onEditOrder,
}: OrderDialogsProps) => {
  return (
    <>
      <ModifyOrderDialog
        active={state.modifyOrderModalActive}
        onDismiss={() => actions.setModifyOrderModalActive(false)}
        orderId={order.id}
      />
      <OrderDeliveredDialog
        active={state.orderDeliveredDialogActive}
        onDismiss={() => actions.setOrderDeliveredDialogActive(false)}
        orderId={order.id}
        retailerId={order.RetailerId}
        invoiceSent={state.invoiceSent}
        invoicePaid={state.invoicePaid}
        isInvoiceCreationStage={
          order.Supplier.invoiceCreationStage === "delivered" ||
          (order.Supplier.invoiceCreationStage === "dispatched" &&
            !order.dispatchedAt)
        }
        paymentType={order.paymentType}
      />
      <OrderPaidModal
        onDismiss={() => actions.setOrderPaidDialogActive(false)}
        active={state.orderPaidDialogActive}
        orderId={order.id}
        paymentType={order.paymentType}
      />
      <IncompleteStripePaymentOrderPaidModal
        onDismiss={() =>
          actions.setOrderWithStripePaymentPaidDialogActive(false)
        }
        active={state.orderWithStripePaymentPaidDialogActive}
        orderId={order.id}
      />
      <CardPaymentWithDepositManualPaymentModal
        onDismiss={() =>
          actions.setCardPaymentWithDepositManualPaymentDialogActive(false)
        }
        active={state.cardPaymentWithDepositManualPaymentDialogActive}
        orderId={order.id}
      />
      <BasicDialog
        dialogActive={state.orderCannotBeEditedActive}
        onDismiss={() => actions.setOrderCannotBeEditedActive(false)}
        title={"This order cannot be edited"}
        description={getOrderCannotBeEditedMessage()}
        icon={"ALERT_CIRCLE_OUTLINE"}
      />
      <SellarCreditCancelWarningDialog
        active={state.sellarCreditCancelWarningActive}
        type={business.Supplier ? "supplier" : "buyer"}
        onEditOrder={
          business.Retailer
            ? () => {
                actions.setSellarCreditCancelWarningActive(false);
                onEditOrder();
              }
            : undefined
        }
        onDismiss={() => {
          actions.setSellarCreditCancelWarningActive(false);
        }}
        onConfirmCancel={() => {
          actions.setSellarCreditCancelWarningActive(false);
          actions.setCancelOrderModalActive(true);
        }}
      />
      <CancelOrderDialog
        active={state.cancelOrderModalActive}
        order={order}
        onDismiss={() => actions.setCancelOrderModalActive(false)}
      />
      <OrderCreateInvoiceDialog
        active={state.invoiceCreateDialogActive}
        orderId={order.id}
        onDismiss={() => actions.setInvoiceCreateDialogActive(false)}
        retailerInvoiceEmails={
          !!order.retailerInvoiceEmails?.length
            ? order.retailerInvoiceEmails
            : sanitizeEmailArray(order.Retailer.Business.invoiceEmails)
        }
      />
      <SellarCreditOrderCreateInvoiceDialog
        onDismiss={() =>
          actions.setSellarCreditInvoiceCreateDialogActive(false)
        }
        open={state.sellarCreditInvoiceCreateDialogActive}
        orderId={order.id}
      />
      <XeroInvoiceDialog
        active={state.xeroInvoiceDialogActive}
        onDismiss={() => actions.setXeroInvoiceDialogActive(false)}
        order={order}
      />
      {connection?.id && (
        <CancelRecreateOrderDialogActive
          active={state.cancelRecreateDialogActive}
          onDismiss={() => actions.setCancelRecreateDialogActive(false)}
          order={order}
          connectionId={connection.id}
        />
      )}
      <CheckoutOrderPartialRefundDialog
        active={state.issueRefundDialogActive}
        onDismiss={() => actions.setIssueRefundDialogActive(false)}
        orderItems={order.OrderItems}
        orderId={order.id}
        buyerName={order.Retailer.Business.name}
        vatPercentage={order.vatPercentage}
        currency={order.currency}
      />
      <OrderEventListDialog
        active={state.eventListDialogActive}
        onDismiss={() => actions.setEventListDialogActive(false)}
        orderId={order.id}
      />
      <CreateCreditNoteDialog
        key={order.id}
        onDismiss={() => actions.setCreateCreditNoteDialogActive(false)}
        active={state.createCreditNoteDialogActive}
        order={order}
      />
      <CreateSellarImportCreditNoteDialog
        key={order.id}
        onDismiss={() =>
          actions.setCreateSellarImportCreditNoteDialogActive(false)
        }
        active={state.createSellarImportCreditNoteDialogActive}
        order={order}
      />
      <AdminChargeRemainingOrderBalance
        key={order.id}
        active={state.adminChargeRemainingOrderBalanceActive}
        orderId={order.id}
        onDismiss={() =>
          actions.setAdminChargeRemainingOrderBalanceActive(false)
        }
      />
      <AdminAddOrderToPallet
        key={order.id}
        active={state.adminAddOrderToPalletDialogActive}
        onDismiss={() => actions.setAdminAddOrderToPalletDialogActive(false)}
        orderId={order.id}
      />
      <AdminRemoveOrderFromPallet
        key={order.id}
        active={state.adminRemoveOrderFromPalletDialogActive}
        onDismiss={() =>
          actions.setAdminRemoveOrderFromPalletDialogActive(false)
        }
        orderId={order.id}
      />
      <OrderAssignRouteAndCreateCTTaskDialog
        key={order.id}
        active={state.adminOrderAssignRouteAndCreateCtTaskActive}
        onDismiss={() =>
          actions.setAdminOrderAssignRouteAndCreateCtTaskActive(false)
        }
        orderId={order.id}
      />
    </>
  );
};
